// AstriaVietnamShell.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { Moon, Calendar, Heart, Home, Layers } from "lucide-react";
import { AstriaVietnamLanguage } from "./astriaVietnamLanguage";
import { ASTRIA_VIETNAM_COPY } from "./astriaVietnamCopy";

// ─── Astria Vietnam — shared shell (header + language toggle + sticky tab
// bar) ──────────────────────────────────────────────────────────────────────
// Distinct visual identity from every other Astria lane (Vi.txt's own UI
// Pack v3 palette: soft pink / rose / ivory), not a reuse of India V2's
// purple/gold — see AstriaVietnamWizard.tsx / AstriaVietnamCard.tsx for the
// re-themed primitives this shell's siblings use.
//
// Bilingual (EN/VI) — `lang`/`onLangChange` are controlled by the parent
// tab component (via useAstriaVietnamLanguage()), same pattern as
// AstriaIndiaV2Shell.tsx, so the shell's toggle and the tab body's copy
// stay in sync from one source of truth.
//
// Distinct from "Astria Vietnam V2" (components/astro/vietnamV2/) — a
// different, already-shipped, non-astrology emotional check-in feature.
// This shell/category is never reachable from that one and vice versa.

export const ASTRIA_VIETNAM_HEADING_FONT = "'Poppins', 'Inter', sans-serif";
export const ASTRIA_VIETNAM_BODY_FONT = "'Inter', sans-serif";
// Shared cap for header/tab-bar/body so the panel fills more of the chat
// column on wide screens instead of floating with large side margins.
const ASTRIA_VIETNAM_MAX_WIDTH = 960;

export const SHELL_COLORS_ASTRIA_VIETNAM = {
  bg: "linear-gradient(160deg, #FFFDFB 0%, #FBF4F7 55%, #FAF7F9 100%)",
  card: "#FFFFFF",
  section: "#F7EEF3",
  cardBorder: "#E2D6DD",
  titleColor: "#3A2F33",
  sectionTitleColor: "#3A2F33",
  bodyColor: "#3A2F33",
  mutedColor: "#6D5F63",
  accent: "#C27BA0",
  accentSoft: "rgba(194,123,160,0.14)",
  accentGlow: "0 0 0 3px rgba(194,123,160,0.18)",
  primary: "#E8C8D8",
  rose: "#E3AFC4",
  white: "#FFFFFF",
};

export type AstriaVietnamTab =
  | "birth-chart"
  | "lucky-day"
  | "compatibility"
  | "space-energy"
  | "tarot";

interface TabDef {
  key: AstriaVietnamTab;
  icon: React.ReactNode;
  path: string;
  accent: [string, string];
}

const TABS: TabDef[] = [
  {
    key: "birth-chart",
    icon: <Moon className="w-4 h-4" />,
    path: "/astria-vietnam/birth-chart",
    accent: ["#C27BA0", "#E8C8D8"],
  },
  {
    key: "lucky-day",
    icon: <Calendar className="w-4 h-4" />,
    path: "/astria-vietnam/lucky-day",
    accent: ["#C9A24B", "#C27BA0"],
  },
  {
    key: "compatibility",
    icon: <Heart className="w-4 h-4" />,
    path: "/astria-vietnam/compatibility",
    accent: ["#C27BA0", "#E3AFC4"],
  },
  {
    key: "space-energy",
    icon: <Home className="w-4 h-4" />,
    path: "/astria-vietnam/space-energy",
    accent: ["#2E8B8B", "#C27BA0"],
  },
  {
    key: "tarot",
    icon: <Layers className="w-4 h-4" />,
    path: "/astria-vietnam/tarot",
    accent: ["#6B3FA0", "#E3AFC4"],
  },
];

// tab key -> copy-file section key, for looking up the translated tabLabel.
const TAB_COPY_KEY: Record<
  AstriaVietnamTab,
  keyof typeof ASTRIA_VIETNAM_COPY["vi"]
> = {
  "birth-chart": "birthChart",
  "lucky-day": "luckyDay",
  compatibility: "compatibility",
  "space-energy": "spaceEnergy",
  tarot: "tarot",
};

const LANGUAGE_TOGGLE_LABELS: Record<AstriaVietnamLanguage, string> = {
  vi: "VI",
  en: "EN",
};

interface AstriaVietnamShellProps {
  active: AstriaVietnamTab;
  lang: AstriaVietnamLanguage;
  onLangChange: (lang: AstriaVietnamLanguage) => void;
  children: React.ReactNode;
}

const AstriaVietnamShell: React.FC<AstriaVietnamShellProps> = ({
  active,
  lang,
  onLangChange,
  children,
}) => {
  const navigate = useNavigate();
  const copy = ASTRIA_VIETNAM_COPY[lang];

  return (
    <div
      className="min-h-screen"
      style={{
        background: SHELL_COLORS_ASTRIA_VIETNAM.bg,
        fontFamily: ASTRIA_VIETNAM_BODY_FONT,
      }}
    >
      {/* ─── Header ──────────────────────────────────────────────────── */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          background: "rgba(255,253,251,0.82)",
          borderBottom: `1px solid ${SHELL_COLORS_ASTRIA_VIETNAM.cardBorder}`,
        }}
      >
        <div
          className="mx-auto flex items-center justify-between gap-3 px-5"
          style={{ maxWidth: ASTRIA_VIETNAM_MAX_WIDTH, paddingTop: 18, paddingBottom: 14 }}
        >
          <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${SHELL_COLORS_ASTRIA_VIETNAM.accent} 0%, ${SHELL_COLORS_ASTRIA_VIETNAM.primary} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 14px ${SHELL_COLORS_ASTRIA_VIETNAM.accentSoft}`,
                flexShrink: 0,
              }}
            >
              <span style={{ fontSize: 16, color: "#fff" }}>✦</span>
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: SHELL_COLORS_ASTRIA_VIETNAM.titleColor,
                  fontFamily: ASTRIA_VIETNAM_HEADING_FONT,
                  letterSpacing: "0.01em",
                }}
              >
                {copy.shell.title}
              </div>
              <div
                style={{
                  fontSize: 11.5,
                  color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor,
                }}
              >
                {copy.shell.subtitle}
              </div>
            </div>
          </div>

          {/* Language toggle — Vietnamese / English */}
          <div
            role="group"
            aria-label={copy.shell.langToggleLabel}
            className="flex items-center flex-shrink-0"
            style={{
              background: SHELL_COLORS_ASTRIA_VIETNAM.section,
              borderRadius: 999,
              padding: 3,
              border: `1px solid ${SHELL_COLORS_ASTRIA_VIETNAM.cardBorder}`,
            }}
          >
            {(Object.keys(LANGUAGE_TOGGLE_LABELS) as AstriaVietnamLanguage[]).map(
              (code) => {
                const isActive = lang === code;
                return (
                  <button
                    key={code}
                    onClick={() => onLangChange(code)}
                    aria-pressed={isActive}
                    className="rounded-full text-xs font-medium transition-all duration-200"
                    style={{
                      paddingInlineStart: 12,
                      paddingInlineEnd: 12,
                      paddingTop: 6,
                      paddingBottom: 6,
                      background: isActive
                        ? SHELL_COLORS_ASTRIA_VIETNAM.accent
                        : "transparent",
                      color: isActive
                        ? SHELL_COLORS_ASTRIA_VIETNAM.white
                        : SHELL_COLORS_ASTRIA_VIETNAM.mutedColor,
                      fontWeight: isActive ? 600 : 500,
                    }}
                  >
                    {LANGUAGE_TOGGLE_LABELS[code]}
                  </button>
                );
              },
            )}
          </div>
        </div>

        {/* ─── Tab bar ─────────────────────────────────────────────────
            Tabs stretch (flex: 1) to fill the full row width evenly — no
            leftover space on the right. `flex-wrap` + `minWidth` is the
            fallback for any viewport too narrow to fit all 5 on one line,
            so it wraps to a 2nd row instead of clipping/scrolling. */}
        <div
          className="mx-auto px-5 pb-3"
          style={{
            maxWidth: ASTRIA_VIETNAM_MAX_WIDTH,
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
          }}
        >
          {TABS.map((tab) => {
            const isActive = tab.key === active;
            const tabCopy = copy[TAB_COPY_KEY[tab.key]] as { tabLabel: string };
            return (
              <button
                key={tab.key}
                onClick={() => navigate(tab.path)}
                className="flex items-center justify-center gap-1.5 rounded-full transition-all whitespace-nowrap"
                style={{
                  flex: "1 1 auto",
                  minWidth: "fit-content",
                  paddingInline: 14,
                  paddingBlock: 8,
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: isActive
                    ? "#FFFFFF"
                    : SHELL_COLORS_ASTRIA_VIETNAM.bodyColor,
                  background: isActive
                    ? `linear-gradient(135deg, ${tab.accent[0]} 0%, ${tab.accent[1]} 100%)`
                    : SHELL_COLORS_ASTRIA_VIETNAM.section,
                  boxShadow: isActive
                    ? `0 4px 14px ${SHELL_COLORS_ASTRIA_VIETNAM.accentSoft}`
                    : "none",
                  border: isActive
                    ? "none"
                    : `1px solid ${SHELL_COLORS_ASTRIA_VIETNAM.cardBorder}`,
                }}
              >
                {tab.icon}
                <span>{tabCopy.tabLabel}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── Body ────────────────────────────────────────────────────── */}
      <div
        className="mx-auto px-4 sm:px-5"
        style={{ maxWidth: ASTRIA_VIETNAM_MAX_WIDTH, paddingTop: 24, paddingBottom: 64 }}
      >
        {children}
      </div>
    </div>
  );
};

export default AstriaVietnamShell;
export { TABS as ASTRIA_VIETNAM_TABS };
